import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Header from '../components/Header';
import { RootState, AppDispatch } from '../store';
import {
	setCurrentLightbox,
	setLanguageChanged,
	setAppView,
	resetIframeHeight,
	setShowFakeDataForm,
	setHeaderHeight,
} from '../store/application/actions';
import {
	Settings,
	DeviceType,
	Orientation,
	WriteDirection,
	Platform,
	RangeSettings,
	AppView,
	SofaRangeName,
	Lightbox,
} from 'sofa-reborn-react';
import { PromptButtonTypeEnum } from '@inter-ikea-kompis/component-prompt';
import type { LanguageSelectorModalEnum } from '@inter-ikea-kompis/component-language-selector';
import { hidePopOver } from '../store/popOver/actions';
import { setShowOpenDesign } from '../store/openDesign/actions';
import { selectSceneItem, setKeepSceneHeight } from '../store/scene/actions';
import { setActionButtonsVisible } from '../store/actionButtons/actions';
import { localeService, UrlService } from '../services';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import { StyledHeaderContainer } from '../components/Header/HeaderStyle';
import { setStoreId } from '../store/localization/actions';
import { ApplicationUtils } from '../util/ApplicationUtils';
import { setCloseGallery } from '../store/sofaConfiguration';

let restartTabFocus = false;

const HeaderContainer: FC<{ onSummaryPage: boolean }> = (props) => {
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const appView = useSelector<RootState, AppView>((state) => state.application.appView);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const actionButtonsVisible = useSelector<RootState, boolean>(
		(state) => state.actionButtons.actionButtonsVisible
	);
	const showScreensaver = useSelector<RootState, boolean>(
		(state) => state.screensaver.showScreensaver
	);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const enableFakeData = UrlService.getApplicationParams().fakeData;
	const noSummaryPageBackButton = useSelector<RootState, boolean>(
		(state) => state.checkout.noSummaryPageBackButton
	);
	const showIpexGalleryButton = useSelector<RootState, boolean>(
		(state) => state.application.showIpexGalleryButton
	);
	const isInUpptackaPlatform = useSelector<RootState, boolean>(
		(state) => state.application.isInUpptackaPlatform
	);
	const footerHeight = useSelector<RootState, number>(
		(state) => state.application.footerHeight as number
	);
	const hasVisitScene = useSelector<RootState, boolean>((state) => state.application.hasVisitScene);
	const showMeasurements = useSelector<RootState, boolean>(
		(state) => state.actionButtons.showMeasurements as boolean
	);
	const showArea = currentLightbox !== 'product-intro-gallery' && appView === 'build-view';
	const features = useSelector<RootState, string[] | null>((state) => state.settings.features);
	const persistedConfiguration = useSelector<RootState, string | null>(
		(state) => state.sofaConfiguration.persistedConfiguration
	);
	const showConfigurationAssistant = features
		? !ApplicationUtils.isProduction() && features.includes('configurationAssistant') && showArea
		: false;

	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const params = UrlService.getApplicationParams();
	const fromNavigator = params.fromNavigator;

	const [visibleIpexGalleryPrompt, setVisibleIpexGalleryPrompt] = useState<boolean>(false);
	const [toolsBottom, setToolsBottom] = useState(0);
	const [visibleModal, setVisibleModal] = useState<LanguageSelectorModalEnum | null>(null);
	const [configurationString, setConfigurationString] = useState<string | null>(null);
	const [updateConfiguration, setUpdateConfiguration] = useState<boolean>(false);

	const dispatch = useDispatch<AppDispatch>();
	const onSummaryPage = props.onSummaryPage;
	const isProduction = ApplicationUtils.isProduction();

	const onStartAgain = (): void => {
		dispatch(
			hidePopOver({
				type: 'disabled-drag',
			})
		);
		dispatch(selectSceneItem(null));
		if (appView === 'summary-page') {
			dispatch(setAppView('build-view'));
			dispatch(setCloseGallery('summary-page'));
			dispatch(resetIframeHeight());
			setTimeout(() => {
				dispatch(setKeepSceneHeight(false));
			}, 100);
			if (platform === 'kiosk') {
				dispatch(setStoreId(UrlService.getStoreId()));
			}
		} else {
			if (isInUpptackaPlatform) {
				window.parent.postMessage(
					{
						method: 'upptacka_backclicked',
					},
					'*'
				);
			}
			dispatch(setCurrentLightbox('product-intro-gallery'));
			restartTabFocus = true;
		}
	};

	const onShowActionButtons = (): void => {
		dispatch(setActionButtonsVisible(!actionButtonsVisible));
	};

	const onModalOpen = (e: CustomEvent): void => {
		setVisibleModal(e.detail.visibleModal);
	};

	const onModalClose = (): void => {
		setVisibleModal(null);
	};

	const onChangeLanguage = (locale: string): void => {
		onModalClose();
		dispatch(setLanguageChanged('build-view'));
		window.location.href = localeService.getUrlWithNewLocale(window.location.href, locale);
	};

	const onCloseOpenDesign = (): void => {
		dispatch(setShowOpenDesign(false));
	};

	const onAddFakeDataClick = (): void => {
		dispatch(setShowFakeDataForm(true));
	};

	const getToolsBottom = (footerHeight: number): void => {
		setToolsBottom(footerHeight - 88 + 200);
	};

	const upptackaCloseButtonClicked = (): void => {
		window.parent.postMessage(
			{
				method: 'upptacka_closeclicked',
			},
			'*'
		);
	};

	const redirectToIpexGallery = (): void => {
		window.location.href = isProduction
			? (localeService.getApplicationSetting('experienceGalleryBackURL') as string)
			: 'https://www.cte.ikeadt.com/addon-app/gallery/latest/';
	};

	const onKeyUp = (e: KeyboardEvent): void => {
		if (restartTabFocus && e.key === 'Tab') {
			const startFromScratchButton = document
				.querySelector('kompis-product-intro-gallery-start-from-scratch')
				?.shadowRoot?.querySelector('kompis-button')
				?.shadowRoot?.querySelector('button');

			startFromScratchButton?.focus();
			restartTabFocus = false;
		}
	};

	const resetSwiper = (): void => {
		if (platform === 'kiosk') {
			const productViewWrapperElement = document.getElementById('product-view');

			if (productViewWrapperElement) {
				productViewWrapperElement.scrollTop = 0;
			}
		}
	};

	const onBackToScene = (): void => {
		dispatch(setCurrentLightbox('none'));
		resetSwiper();

		dispatch(setCloseGallery('product-intro-gallery'));
	};

	useEffect(() => {
		getToolsBottom(footerHeight);
	}, [footerHeight]);

	useEffect(() => {
		const onResize = (element: ResizeObserverEntry[]): void => {
			dispatch(setHeaderHeight((element[0].target as HTMLElement).offsetHeight));
		};
		const resizeObserver = new ResizeObserver(onResize);
		const elements = Array.from(
			document.getElementsByClassName('header'),
			(header) => header as HTMLElement
		);
		for (const element of elements) {
			if (element && element.offsetHeight) {
				dispatch(setHeaderHeight(element.offsetHeight));
				resizeObserver.observe(element);
			}
		}

		document.addEventListener('keyup', onKeyUp);

		return (): void => {
			for (const element of elements) {
				if (element && element.offsetHeight) {
					resizeObserver.unobserve(element);
				}
			}
			document.removeEventListener('keyup', onKeyUp);
		};
	}, []);

	useEffect(() => {
		if (showScreensaver) {
			setVisibleModal(null);
		}
	}, [showScreensaver]);

	useEffect(() => {
		setUpdateConfiguration(true);
		if (persistedConfiguration && !persistedConfiguration.includes('EMPTY')) {
			setConfigurationString(
				persistedConfiguration.substring(persistedConfiguration.indexOf(':') + 1).slice(0, -1)
			);
		} else {
			setConfigurationString(null);
		}
		setTimeout(() => {
			setUpdateConfiguration(false);
		}, 1000);
	}, [persistedConfiguration]);

	const platformUrlPart = isInUpptackaPlatform ? 'origin=upptacka' : 'ipexGallery=true';
	const navigatorUrl = `https://www.ikea.com/addon-app/rangenavigator/latest/${localeService.getCountryCode()}/${localeService.getLanguageCode()}/?range=${UrlService.getSofaRange()}&${platformUrlPart}#${UrlService.getVpcOrSprId()}`;

	return (
		<StyledHeaderContainer className="header" noBack={noSummaryPageBackButton}>
			<Header
				settings={settings}
				deviceType={deviceType}
				orientation={orientation}
				platform={platform}
				appView={appView}
				toolsBottom={toolsBottom}
				writeDirection={writeDirection}
				sofaRangeName={rangeSettings.rangeName as SofaRangeName}
				visibleModal={visibleModal}
				showArea={showArea}
				hasVisitScene={hasVisitScene}
				showMeasurements={showMeasurements}
				showConfigurationAssistant={showConfigurationAssistant}
				configurationString={configurationString}
				updateConfiguration={updateConfiguration}
				fromNavigator={fromNavigator}
				navigatorUrl={navigatorUrl}
				onStartAgain={onStartAgain}
				onShowActionButtons={onShowActionButtons}
				onModalOpen={onModalOpen}
				onModalClose={onModalClose}
				onChangeLanguage={onChangeLanguage}
				showActionButtons={actionButtonsVisible}
				showScreensaver={showScreensaver}
				currentLightbox={currentLightbox}
				showIpexGalleryButton={showIpexGalleryButton}
				isInUpptackaPlatform={isInUpptackaPlatform}
				onBackToScene={onBackToScene}
				onCloseOpenDesign={onCloseOpenDesign}
				onAddFakeDataClick={onAddFakeDataClick}
				enableFakeData={enableFakeData && settings.environment !== 'production'}
				noSummaryPageBackButton={noSummaryPageBackButton}
				kompisSettings={kompisSettings}
				kompisTranslations={kompisTranslations}
				onSummaryPage={onSummaryPage}
				onUpptackaCloseButtonClicked={upptackaCloseButtonClicked}
				visibleIpexGalleryPrompt={visibleIpexGalleryPrompt}
				onIpexGalleryButtonClick={() => {
					if (hasVisitScene) {
						setVisibleIpexGalleryPrompt(true);
					} else {
						redirectToIpexGallery();
					}
				}}
				onIpexGalleryPromptButtonClick={(event: CustomEvent): void => {
					if (event.detail.type === PromptButtonTypeEnum.primaryButton) {
						setVisibleIpexGalleryPrompt(false);
					} else {
						redirectToIpexGallery();
					}
				}}
			></Header>
		</StyledHeaderContainer>
	);
};
export default HeaderContainer;
